/**
 * FDR → COA (operator order 2026-08-21, final form): the REAL work plan —
 * the existing COA engine mounted here with scope="fdr", the same
 * one-engine pattern as Audits — plus Scorecards as its own view.
 * Nothing is recreated; internal COA behavior is untouched.
 *
 * Scorecards: the organization's canonical workbook ingests
 * deterministically; misses flag red; benchmarks come from the file or are
 * set here (user-set wins and survives re-uploads); every cell is editable.
 *
 * Compare (operator order 2026-08-21 evening): vendors fill the SAME
 * canonical template, so KPIs match automatically by normalized text; the
 * user can additionally PAIR metrics by hand ("these are the same measure")
 * and those pairings persist and ride the grid as one row. The roster reads
 * like a standings board: met rate, direction, and the year's sparkline.
 * The scorecard itself opens in a modal and can be saved as Excel or PDF.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import COAWorkPlan from '../coa/COAWorkPlan';
import FdrCharting from './FdrCharting';

const API = process.env.REACT_APP_API_URL;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const VENDOR_COLORS = ['#0e7490', '#ea580c', '#6d28d9', '#0f766e', '#b45309', '#be185d', '#1d4ed8', '#4d7c0f'];

const jfetch = async (path, opts = {}) => {
  const r = await fetch(`${API}/api/fdr${path}`, { credentials: 'include', ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d?.message || 'Request failed');
  return d;
};

// A benchmark typed by a person: "95", "95%" or "0.95" all mean 95 percent
// for a percent KPI; plain numbers stay numbers. Yes/No KPIs take the answer
// that counts as good.
export function parseBenchmarkInput(kind, text) {
  const t = String(text ?? '').trim();
  if (kind === 'yesno') {
    const v = t.toLowerCase();
    if (v === 'yes' || v === 'y') return { benchmark_op: 'yes', benchmark_value: null };
    if (v === 'no' || v === 'n') return { benchmark_op: 'no', benchmark_value: null };
    return null;
  }
  if (t === '') return { benchmark_op: null, benchmark_value: null };
  const n = Number(t.replace('%', ''));
  if (!Number.isFinite(n)) return null;
  if (kind === 'percent') return { benchmark_op: '>=', benchmark_value: t.includes('%') || n > 1 ? n / 100 : n };
  return { benchmark_op: '>=', benchmark_value: n };
}

// Change ONE vendor's benchmark, with the reason that gets saved with it
// (operator order 2026-09-18). The next monthly upload keeps it.
function BenchmarkEditor({ metric: m, vendorName, onCancel, onSaved }) {
  const [val, setVal] = useState(() => m.kind === 'yesno'
    ? (m.benchmark_op === 'no' ? 'No' : 'Yes')
    : m.benchmark_value == null ? '' : (m.kind === 'percent' ? String(Math.round(Number(m.benchmark_value) * 1000) / 10) : String(m.benchmark_value)));
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [history, setHistory] = useState(null);
  const save = async () => {
    const parsed = parseBenchmarkInput(m.kind, val);
    if (!parsed) { setErr(m.kind === 'yesno' ? 'Choose Yes or No.' : 'Enter a number.'); return; }
    if (why.trim().length < 5) { setErr('Write the reason. It is saved with the change.'); return; }
    setBusy(true); setErr('');
    try {
      await jfetch(`/scorecards/metrics/${m.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...parsed, rationale: why.trim() }) });
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  const loadHistory = () => jfetch(`/scorecards/metrics/${m.id}/history`).then((d) => setHistory(d.history || [])).catch(() => setHistory([]));
  const fmt = (op, value) => op === 'yes' ? 'Yes' : op === 'no' ? 'No' : value == null ? 'none' : (m.kind === 'percent' ? `${op || '>='} ${Math.round(Number(value) * 1000) / 10}%` : `${op || '>='} ${value}`);
  return (
    <div className="w-72 p-2.5 rounded-lg border border-orange-300 bg-white shadow-sm text-[11px] space-y-1.5" onClick={(e) => e.stopPropagation()}>
      <div className="font-semibold text-slate-700">Benchmark for {vendorName} only</div>
      {m.kind === 'yesno' ? (
        <div className="flex gap-1.5">
          {['Yes', 'No'].map((o) => (
            <button key={o} type="button" onClick={() => setVal(o)}
              className={`px-2.5 py-1 rounded-md font-semibold ${val === o ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600'}`}>{o} is the good answer</button>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">At least</span>
          <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} className="w-20 border border-slate-300 rounded px-1.5 py-0.5" />
          {m.kind === 'percent' && <span className="text-slate-500">%</span>}
        </div>
      )}
      <textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={2} placeholder="Why this vendor gets this benchmark (required, saved with the change)"
        className="w-full border border-slate-300 rounded px-1.5 py-1" />
      {m.benchmark_source === 'user' && (
        <div className="text-slate-500">
          Now: {fmt(m.benchmark_op, m.benchmark_value)}{m.benchmark_set_by_name ? ` · set by ${m.benchmark_set_by_name}` : ''}{m.benchmark_set_at ? ` on ${new Date(m.benchmark_set_at).toLocaleDateString()}` : ''}
          {m.benchmark_rationale ? <div className="italic">“{m.benchmark_rationale}”</div> : null}
        </div>
      )}
      {err && <div className="text-rose-600">{err}</div>}
      <div className="flex items-center gap-2">
        <button type="button" disabled={busy} onClick={save} className="px-2.5 py-1 rounded-md bg-orange-500 text-white font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={onCancel} className="text-slate-500 hover:text-slate-700">Cancel</button>
        <button type="button" onClick={loadHistory} className="ml-auto text-slate-400 hover:text-slate-700">History</button>
      </div>
      {history && (
        <div className="border-t border-slate-100 pt-1.5 space-y-1 max-h-40 overflow-y-auto">
          {history.length === 0 ? <div className="text-slate-400">No changes recorded yet.</div> : history.map((h) => (
            <div key={h.id} className="text-slate-600">
              <span className="font-semibold">{fmt(h.old_op, h.old_value)} → {fmt(h.new_op, h.new_value)}</span> · {h.changed_by_name || 'unknown'} · {new Date(h.changed_at).toLocaleDateString()}
              <div className="italic text-slate-500">“{h.rationale}”</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// What the rules say about one month, in words.
const autoOutcomeText = (s) => {
  if (!s) return 'no value';
  if (s.met_auto === true) return 'met';
  if (s.met_auto === false) return 'missed';
  return s.gated_by_metric_id ? 'not applicable (the count above is 0)' : 'no verdict';
};
const OUTCOME_LABEL = { auto: 'Automatic', met: 'Met', missed: 'Missed', na: 'Not applicable' };

// One month of one KPI (operator order 2026-10-20): the value, and the
// outcome a person may set over the rules, with the note that is saved
// with it. The computed outcome stays in view so the two never blur.
function CellEditor({ metric: m, year, month, score: s, gateKpi, anchor, onCancel, onSaved }) {
  const [val, setVal] = useState(s?.value_raw ?? '');
  const [outcome, setOutcome] = useState(s?.outcome_override || 'auto');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [history, setHistory] = useState(null);
  const current = s?.outcome_override || 'auto';
  const outcomeChanges = outcome !== current;
  const valueChanges = String(val ?? '').trim() !== String(s?.value_raw ?? '').trim();
  const save = async () => {
    if (outcomeChanges && note.trim().length < 5) { setErr('Write why the outcome is being changed. It is saved with the change.'); return; }
    if (!outcomeChanges && !valueChanges) { onCancel(); return; }
    setBusy(true); setErr('');
    try {
      const body = { metric_id: m.id, year, month, note: note.trim() };
      if (valueChanges) body.value = val;
      if (outcomeChanges) body.outcome = outcome;
      await jfetch('/scorecards/scores', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  const loadHistory = () => jfetch(`/scorecards/metrics/${m.id}/outcome-history?year=${year}`)
    .then((d) => setHistory((d.history || []).filter((h) => h.month === month))).catch(() => setHistory([]));
  const fmtOutcome = (o) => (o ? OUTCOME_LABEL[o] : 'Automatic');
  return (
    <div className="fixed z-[60] w-80 p-2.5 rounded-lg border border-orange-300 bg-white shadow-lg text-[11px] text-left space-y-1.5 cursor-default"
      style={{ left: Math.min(Math.max(170, anchor?.x ?? 170), (typeof window !== 'undefined' ? window.innerWidth : 1200) - 170) - 160, top: Math.min(anchor?.y ?? 0, (typeof window !== 'undefined' ? window.innerHeight : 800) - 360) }}
      onClick={(e) => e.stopPropagation()}>
      <div className="font-semibold text-slate-700">{MONTHS[month - 1]} {year}</div>
      <div className="text-slate-500 leading-snug">{m.kpi_text}</div>
      <div className="flex items-center gap-1.5">
        <span className="text-slate-500 w-14">Value</span>
        <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onCancel(); }}
          placeholder={m.kind === 'yesno' ? 'Yes or No' : m.kind === 'percent' ? 'e.g. 95%' : ''}
          className="w-24 border border-slate-300 rounded px-1.5 py-0.5" />
        <span className="text-slate-400">rules say: {autoOutcomeText(s)}</span>
      </div>
      {gateKpi && <div className="text-slate-400 leading-snug">Applies only when “{gateKpi}” is above 0.</div>}
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-slate-500 w-14">Outcome</span>
        {['auto', 'met', 'missed', 'na'].map((o) => (
          <button key={o} type="button" onClick={() => setOutcome(o)}
            className={`px-2 py-0.5 rounded-md font-semibold ${outcome === o ? (o === 'missed' ? 'bg-rose-600 text-white' : o === 'met' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-white') : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {OUTCOME_LABEL[o]}
          </button>
        ))}
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder={outcomeChanges ? 'Why the outcome is being changed (required, saved with the change)' : 'Note (optional)'}
        className="w-full border border-slate-300 rounded px-1.5 py-1" />
      {s?.outcome_override && (
        <div className="text-slate-500">
          Now: {fmtOutcome(s.outcome_override)}{s.outcome_set_by_name ? ` · set by ${s.outcome_set_by_name}` : ''}{s.outcome_set_at ? ` on ${new Date(s.outcome_set_at).toLocaleDateString()}` : ''}
          {s.outcome_note ? <div className="italic">“{s.outcome_note}”</div> : null}
        </div>
      )}
      {err && <div className="text-rose-600">{err}</div>}
      <div className="flex items-center gap-2">
        <button type="button" disabled={busy} onClick={save} className="px-2.5 py-1 rounded-md bg-orange-500 text-white font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={onCancel} className="text-slate-500 hover:text-slate-700">Cancel</button>
        <button type="button" onClick={loadHistory} className="ml-auto text-slate-400 hover:text-slate-700">History</button>
      </div>
      {history && (
        <div className="border-t border-slate-100 pt-1.5 space-y-1 max-h-40 overflow-y-auto">
          {history.length === 0 ? <div className="text-slate-400">No changes recorded for this month.</div> : history.map((h) => (
            <div key={h.id} className="text-slate-600">
              <span className="font-semibold">{fmtOutcome(h.old_override)} → {fmtOutcome(h.new_override)}</span>
              {h.old_value_raw !== h.new_value_raw ? ` · value ${h.old_value_raw ?? '—'} → ${h.new_value_raw ?? '—'}` : ''} · {h.changed_by_name || 'unknown'} · {new Date(h.changed_at).toLocaleDateString()}
              <div className="italic text-slate-500">“{h.note}”</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The rule behind a Yes/No obligation (operator order 2026-10-20): which
// count or screener on the sheet makes it not applicable when it reads 0.
// Automatic follows the workbook's layout; compliance may pin another KPI
// or say the obligation always applies, with a reason.
function GateEditor({ metric: m, gates, kpiById, vendorName, onCancel, onSaved }) {
  const [mode, setMode] = useState(m.gate_mode || 'auto');
  const [gateId, setGateId] = useState(m.gate_metric_id || '');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const autoText = m.auto_gate_metric_id ? kpiById.get(m.auto_gate_metric_id) : null;
  const save = async () => {
    if (mode === 'metric' && !gateId) { setErr('Pick the KPI that gates this one.'); return; }
    if (why.trim().length < 5) { setErr('Write the reason. It is saved with the change.'); return; }
    setBusy(true); setErr('');
    try {
      await jfetch(`/scorecards/metrics/${m.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ gate_mode: mode, gate_metric_id: mode === 'metric' ? gateId : null, rationale: why.trim() }) });
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <div className="mt-1 w-80 p-2.5 rounded-lg border border-orange-300 bg-white shadow-sm text-[11px] space-y-1.5" onClick={(e) => e.stopPropagation()}>
      <div className="font-semibold text-slate-700">When does this KPI apply? ({vendorName} only)</div>
      <select value={mode === 'metric' ? `metric:${gateId}` : mode} onChange={(e) => { const v = e.target.value; if (v.startsWith('metric:')) { setMode('metric'); setGateId(v.slice(7)); } else { setMode(v); setGateId(''); } }}
        className="w-full border border-slate-300 rounded px-1.5 py-1 bg-white">
        <option value="auto">Automatic: {autoText ? `only when “${autoText.slice(0, 60)}” is above 0` : 'always applies (no count precedes it)'}</option>
        <option value="none">Always applies, whatever the counts say</option>
        {gates.filter((g) => g.id !== m.id).map((g) => <option key={g.id} value={`metric:${g.id}`}>Only when “{g.kpi_text.slice(0, 70)}” is above 0</option>)}
      </select>
      <textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={2} placeholder="Why this rule for this vendor (required, saved with the change)"
        className="w-full border border-slate-300 rounded px-1.5 py-1" />
      {m.gate_mode && m.gate_mode !== 'auto' && (
        <div className="text-slate-500">Now: {m.gate_mode === 'none' ? 'always applies' : `gated by “${kpiById.get(m.gate_metric_id) || '?'}”`}{m.gate_set_by_name ? ` · set by ${m.gate_set_by_name}` : ''}{m.gate_set_at ? ` on ${new Date(m.gate_set_at).toLocaleDateString()}` : ''}
          {m.gate_rationale ? <div className="italic">“{m.gate_rationale}”</div> : null}</div>
      )}
      {err && <div className="text-rose-600">{err}</div>}
      <div className="flex items-center gap-2">
        <button type="button" disabled={busy} onClick={save} className="px-2.5 py-1 rounded-md bg-orange-500 text-white font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={onCancel} className="text-slate-500 hover:text-slate-700">Cancel</button>
      </div>
    </div>
  );
}

function MissBadge({ missed }) {
  if (!missed) return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">All met</span>;
  return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-700">{missed} missed</span>;
}

/** Tiny inline sparkline. values: array of numbers 0..1 (null = gap). */
function Spark({ values, color = '#047857', width = 240, height = 36 }) {
  const pts = values.map((v, i) => (v == null ? null : [i, v])).filter(Boolean);
  if (pts.length < 2) return <div style={{ height }} />;
  const xs = (i) => (pts.length === 1 ? width / 2 : (i / (values.length - 1)) * (width - 8) + 4);
  const ys = (v) => height - 5 - v * (height - 10);
  const path = pts.map(([i, v]) => `${xs(i)},${ys(v)}`).join(' ');
  const [li, lv] = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height }} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={path} fill="none" stroke={color} strokeWidth="2" />
      <circle cx={xs(li)} cy={ys(lv)} r="3" fill={color} />
    </svg>
  );
}

const rateFor = (m) => (m && m.total > 0 ? m.met / m.total : null);
const pctText = (r) => (r == null ? '—' : `${Math.round(r * 100)}%`);

/** Latest month with data + rate, and the delta vs the month before it. */
function latestRate(monthly) {
  const withData = (monthly || []).filter((m) => m.total > 0).sort((a, b) => a.month - b.month);
  if (!withData.length) return { rate: null, month: null, delta: null };
  const last = withData[withData.length - 1];
  const prev = withData.length > 1 ? withData[withData.length - 2] : null;
  const rate = rateFor(last);
  const prevRate = prev ? rateFor(prev) : null;
  return { rate, month: last.month, delta: prevRate == null ? null : rate - prevRate };
}

/** Year to date: every cell with a verdict, across every month with data. */
export function yearToDateRate(monthly) {
  let met = 0; let total = 0;
  for (const m of monthly || []) { met += m.met || 0; total += m.total || 0; }
  return { met, total, rate: total > 0 ? met / total : null };
}

function DeltaArrow({ delta }) {
  if (delta == null || Math.abs(delta) < 0.0005) return null;
  const up = delta > 0;
  return (
    <span className={`text-[11.5px] font-bold ${up ? 'text-emerald-700' : 'text-rose-700'}`}>
      {up ? '▲' : '▼'} {Math.abs(Math.round(delta * 1000) / 10)}
    </span>
  );
}

const benchLabelOf = (m) => {
  if (m.benchmark_op === 'yes') return 'Yes';
  if (m.benchmark_op === 'no') return 'No';
  if (m.benchmark_value == null) return null;
  const v = Number(m.benchmark_value);
  return `${m.benchmark_op || '>='} ${m.kind === 'percent' ? Math.round(v * 1000) / 10 + '%' : v}`;
};

// ---------------------------------------------------------------------------
// Scorecard detail — A MODAL: the workbook alive. Sheet tabs, months across,
// misses red, benchmarks and cells editable, per-KPI trend, save as
// Excel/PDF, compare-with entry point.
// ---------------------------------------------------------------------------
function ScorecardDetail({ vendor, vendors, year, onYear, onChanged, onClose, onCompareWith }) {
  const [data, setData] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [editingBench, setEditingBench] = useState(null);
  const [benchVal, setBenchVal] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editingGate, setEditingGate] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [showUploads, setShowUploads] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ kpi_text: '', kind: 'number', benchmark_value: '' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState('');

  const load = useCallback(() => {
    jfetch(`/scorecards/${vendor.id}?year=${year}`).then((d) => {
      setData(d);
      setSheet((s) => s || (d.metrics[0]?.sheet_name ?? null));
    }).catch((e) => setErr(e.message));
  }, [vendor.id, year]);
  useEffect(() => { load(); }, [load]);

  const scoreMap = useMemo(() => {
    const m = new Map();
    for (const s of (data?.scores || [])) m.set(`${s.metric_id}:${s.month}`, s);
    return m;
  }, [data]);

  const cellText = (m, s) => {
    if (!s) return '';
    if (m.kind === 'percent' && s.value_num != null) return `${Math.round(Number(s.value_num) * 1000) / 10}%`;
    return s.value_raw ?? '';
  };

  /** Per-metric trend: numeric values normalized within the metric; yes/no as 1/0. */
  const trendValues = (m) => {
    const vals = [];
    for (let mo = 1; mo <= 12; mo++) {
      const s = scoreMap.get(`${m.id}:${mo}`);
      vals.push(s && s.value_num != null ? Number(s.value_num) : null);
    }
    const nums = vals.filter((v) => v != null);
    if (nums.length < 2) return null;
    const min = Math.min(...nums); const max = Math.max(...nums);
    const span = max - min;
    return vals.map((v) => (v == null ? null : (span === 0 ? 0.5 : (v - min) / span)));
  };
  const trendColor = (m) => {
    for (let mo = 12; mo >= 1; mo--) {
      const s = scoreMap.get(`${m.id}:${mo}`);
      if (s && s.met === false) return '#be123c';
      if (s && s.met === true) return '#047857';
    }
    return '#64748b';
  };

  const savePdf = async () => {
    if (!data) return;
    setSaving('pdf');
    try {
      const { exportScorecardPdf } = await import('./exportScorecardPdf');
      exportScorecardPdf({ vendorName: vendor.name, year, metrics: data.metrics, scores: data.scores });
    } catch (e) { setErr(e.message); }
    setSaving('');
  };

  const others = (vendors || []).filter((v) => v.id !== vendor.id && (v.cells > 0 || v.last_upload_at));
  const latestUpload = data?.uploads?.[0] || null;
  const metCount = (data?.scores || []).filter((s) => s.met === true).length;
  const missCount = (data?.scores || []).filter((s) => s.met === false).length;

  const body = !data ? <div className="text-xs text-slate-400 py-10 text-center">Loading scorecard…</div> : (() => {
    const sheets = [...new Set(data.metrics.map((m) => m.sheet_name))];
    const visible = data.metrics.filter((m) => m.sheet_name === sheet && (showHidden || !m.hidden));
    const kpiById = new Map(data.metrics.map((m) => [m.id, m.kpi_text]));
    const sheetGates = data.metrics.filter((m) => m.sheet_name === sheet && m.is_gate);
    const gateTextOf = (m) => (m.effective_gate_metric_id ? kpiById.get(m.effective_gate_metric_id) : null);
    const cellTitle = (m, s) => {
      if (!s) return 'Click to enter a value or set the outcome';
      const bits = [];
      if (s.outcome_override) bits.push(`Outcome set by hand: ${OUTCOME_LABEL[s.outcome_override]}${s.outcome_set_by_name ? ` by ${s.outcome_set_by_name}` : ''}${s.outcome_note ? `. ${s.outcome_note}` : ''}. Rules say: ${autoOutcomeText(s)}.`);
      else if (s.gated_by_metric_id) bits.push(`Not applicable this month: “${kpiById.get(s.gated_by_metric_id) || 'the count above'}” is 0. No verdict.`);
      if (s.source === 'user') bits.push('Entered by hand — uploads never overwrite it');
      return bits.join(' ') || 'Click to change the value or the outcome';
    };
    return (
      <>
        <div className="px-5 pt-3 flex items-center gap-2 flex-wrap">
          {sheets.map((s) => (
            <button key={s} onClick={() => setSheet(s)}
              className={`px-2.5 py-1 rounded-lg text-[11.5px] font-semibold ${sheet === s ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {s}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2.5">
            <button onClick={() => setShowHidden((v) => !v)} className="text-[11px] font-semibold text-slate-400 hover:text-slate-600">
              {showHidden ? 'Hide hidden' : 'Show hidden'}
            </button>
            <button onClick={() => setAddOpen((v) => !v)} className="text-[11px] font-semibold text-orange-700 hover:text-orange-900">+ Add metric</button>
          </div>
        </div>
        {err && <div className="px-5 mt-2 text-[11px] font-semibold text-rose-600">{err}</div>}

        {addOpen && (
          <div className="mx-5 mt-2.5 bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[16rem]">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Metric</div>
              <input value={addForm.kpi_text} onChange={(e) => setAddForm({ ...addForm, kpi_text: e.target.value })}
                placeholder="e.g. Percentage % of appeals acknowledged within 5 days"
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-orange-400" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Type</div>
              <select value={addForm.kind} onChange={(e) => setAddForm({ ...addForm, kind: e.target.value })}
                className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                <option value="number">Number</option><option value="percent">Percent</option><option value="yesno">Yes/No</option><option value="date">Date</option>
              </select>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Benchmark</div>
              <input value={addForm.benchmark_value} onChange={(e) => setAddForm({ ...addForm, benchmark_value: e.target.value })}
                placeholder={addForm.kind === 'percent' ? 'e.g. 0.95' : 'optional'}
                className="w-24 text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
            <button onClick={async () => {
              try {
                await jfetch(`/scorecards/${vendor.id}/metrics`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ sheet_name: sheet || 'General', kpi_text: addForm.kpi_text, kind: addForm.kind,
                    benchmark_op: addForm.kind === 'yesno' ? 'yes' : (addForm.benchmark_value ? '>=' : null),
                    benchmark_value: addForm.kind === 'yesno' ? null : addForm.benchmark_value }),
                });
                setAddOpen(false); setAddForm({ kpi_text: '', kind: 'number', benchmark_value: '' }); load(); onChanged();
              } catch (e) { setErr(e.message); }
            }} disabled={!addForm.kpi_text.trim()}
              className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">Add</button>
          </div>
        )}

        <div className="mt-3 px-5 overflow-x-auto flex-1 min-h-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="text-left py-1.5 pr-2 w-36">Category</th>
                <th className="text-left py-1.5 pr-2 min-w-[15rem]">KPI</th>
                <th className="text-left py-1.5 pr-2 w-20">Benchmark</th>
                <th className="text-left py-1.5 pr-2 w-24">Trend</th>
                {MONTHS.map((mo) => <th key={mo} className="text-center py-1.5 px-1 w-12">{mo}</th>)}
              </tr>
            </thead>
            <tbody>
              {visible.map((m, i) => {
                const prevCat = i > 0 ? visible[i - 1].category : null;
                const tv = trendValues(m);
                return (
                  <tr key={m.id} className={`border-b border-slate-100 ${m.hidden ? 'opacity-40' : ''}`}>
                    <td className="py-1.5 pr-2 align-top text-slate-500 font-semibold">{m.category !== prevCat ? m.category : ''}</td>
                    <td className="py-1.5 pr-2 align-top text-slate-700 leading-snug">
                      {m.kpi_text}
                      <button title={m.hidden ? 'Unhide' : 'Hide from the scorecard'} onClick={async () => {
                        await jfetch(`/scorecards/metrics/${m.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ hidden: !m.hidden }) });
                        load();
                      }} className="ml-1.5 text-slate-300 hover:text-slate-500 text-[10px]">{m.hidden ? 'unhide' : 'hide'}</button>
                      {(m.kind === 'yesno' && m.benchmark_op === 'yes') && (
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          {gateTextOf(m)
                            ? <>applies only when “{String(gateTextOf(m)).slice(0, 70)}” is above 0</>
                            : (m.gate_mode === 'none' ? 'always applies' : null)}
                          <button type="button" onClick={() => setEditingGate(editingGate === m.id ? null : m.id)}
                            title="When does this KPI apply? Automatic from the sheet, pinned to a count, or always."
                            className={`${gateTextOf(m) || m.gate_mode === 'none' ? 'ml-1.5' : ''} text-slate-300 hover:text-orange-600`}>rule</button>
                        </div>
                      )}
                      {editingGate === m.id && (
                        <GateEditor metric={m} gates={sheetGates} kpiById={kpiById} vendorName={vendor.name}
                          onCancel={() => setEditingGate(null)}
                          onSaved={() => { setEditingGate(null); load(); onChanged(); }} />
                      )}
                    </td>
                    <td className="py-1.5 pr-2 align-top">
                      {editingBench === m.id ? (
                        <BenchmarkEditor metric={m} vendorName={vendor.name}
                          onCancel={() => setEditingBench(null)}
                          onSaved={() => { setEditingBench(null); load(); onChanged(); }} />
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <button onClick={() => setEditingBench(m.id)}
                            title={m.benchmark_source === 'user'
                              ? `Set for ${vendor.name} only${m.benchmark_set_by_name ? ` by ${m.benchmark_set_by_name}` : ''}${m.benchmark_set_at ? ` on ${new Date(m.benchmark_set_at).toLocaleDateString()}` : ''}${m.benchmark_rationale ? `. Reason: ${m.benchmark_rationale}` : ''}; a re-upload never changes it. Click to change.`
                              : (m.benchmark_source === 'file' ? 'From the vendor workbook. Click to set this vendor\'s own benchmark, with a reason.' : 'No benchmark. Click to set one, with a reason.')}
                            className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${benchLabelOf(m) ? (m.benchmark_source === 'user' ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-600') : 'text-slate-300 hover:text-slate-500'}`}>
                            {benchLabelOf(m) || 'set'}
                          </button>
                          <button onClick={() => setEditingBench(m.id)} title="Change this vendor's benchmark" className="text-slate-300 hover:text-orange-600 text-[10px]">change</button>
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2 align-top">
                      {tv ? <Spark values={tv} color={trendColor(m)} width={90} height={22} /> : <span className="text-slate-200">—</span>}
                    </td>
                    {MONTHS.map((mo, mi) => {
                      const s = scoreMap.get(`${m.id}:${mi + 1}`);
                      const key = `${m.id}:${mi + 1}`;
                      const gated = !!(s && !s.outcome_override && s.gated_by_metric_id);
                      const tone = s?.met === false ? 'bg-rose-50 text-rose-700 font-semibold' : s?.met === true ? 'text-emerald-700' : gated ? 'text-slate-400 italic' : 'text-slate-600';
                      return (
                        <td key={mo} onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setEditingCell(editingCell?.key === key ? null : { key, x: r.left + r.width / 2, y: r.bottom + 4 }); }}
                          title={cellTitle(m, s)}
                          className={`text-center px-0.5 py-1 cursor-pointer rounded ${tone} ${s?.outcome_override ? 'ring-1 ring-inset ring-violet-300' : ''} hover:outline hover:outline-1 hover:outline-orange-300`}>
                          {cellText(m, s)}{s?.source === 'user' ? <span className="text-violet-400">·</span> : ''}{s?.outcome_override ? <span className="text-violet-500 text-[9px] align-super">✎</span> : ''}
                          {editingCell?.key === key && (
                            <CellEditor metric={m} year={year} month={mi + 1} score={s} gateKpi={gateTextOf(m)} anchor={editingCell}
                              onCancel={() => setEditingCell(null)}
                              onSaved={() => { setEditingCell(null); load(); onChanged(); }} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mx-5 mt-2 border-t border-slate-100 pt-2 flex items-center gap-4 flex-wrap text-[11px] text-slate-400">
          <button onClick={() => setShowUploads((v) => !v)} className="font-semibold text-slate-500 hover:text-slate-700">
            Uploads · {data.uploads.length} {showUploads ? '▴' : '▾'}
          </button>
          {latestUpload?.attestation?.signee && <span>Attested by {latestUpload.attestation.signee}{latestUpload.attestation.title ? ` · ${latestUpload.attestation.title}` : ''}</span>}
          <span className="text-slate-300">Red = missed benchmark · grey italic = not applicable, the count above is 0 · violet dot = entered by hand · ✎ = outcome set by hand, with a note</span>
        </div>
        {showUploads && (
          <div className="mx-5 mt-2 space-y-1.5">
            {data.uploads.map((u) => (
              <div key={u.id} className="bg-slate-50 rounded-lg border border-slate-200 px-3 py-2 text-[11.5px] text-slate-600">
                <span className="font-semibold text-slate-700">{u.file_name}</span> · {u.year} · {new Date(u.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                {u.stats ? ` · ${u.stats.cells} cells, ${u.stats.missed} missed` : ''}
                {u.rca_text ? <details className="mt-1"><summary className="cursor-pointer text-slate-500 font-semibold">Root cause analysis (from the workbook)</summary><div className="whitespace-pre-wrap mt-1 text-slate-600">{u.rca_text.slice(0, 3000)}</div></details> : null}
              </div>
            ))}
          </div>
        )}
      </>
    );
  })();

  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(72rem,96vw)] max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div>
            <div className="text-[15px] font-bold text-slate-900">{vendor.name}</div>
            <div className="text-[11.5px] text-slate-500">
              {data ? `${metCount} met · ${missCount} missed` : '…'}
              {vendor.last_upload_at ? ` · received ${new Date(vendor.last_upload_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
            </div>
          </div>
          <select value={year} onChange={(e) => onYear(Number(e.target.value))}
            className="text-[11.5px] border border-slate-200 rounded-lg px-2 py-1 bg-white">
            {[year + 1, year, year - 1, year - 2].filter((y, i, a) => a.indexOf(y) === i).sort((a, b) => b - a).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {others.length > 0 && (
            <select value="" onChange={(e) => { if (e.target.value) onCompareWith(e.target.value); }}
              className="text-[11.5px] border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-600">
              <option value="">Compare with…</option>
              {others.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          )}
          <button onClick={onClose} aria-label="Close" className="ml-auto w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 text-sm">✕</button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto pb-3 flex flex-col">{body}</div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center gap-2 flex-wrap">
          <a href={`${API}/api/fdr/scorecards/${vendor.id}/export.xlsx?year=${year}`}
            className="px-3.5 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold no-underline">
            Save as Excel
          </a>
          <button onClick={savePdf} disabled={saving === 'pdf' || !data}
            className="px-3.5 py-1.5 rounded-lg bg-white border border-slate-300 hover:border-slate-400 text-slate-700 text-xs font-semibold disabled:opacity-50">
            {saving === 'pdf' ? 'Building…' : 'Save as PDF'}
          </button>
          <span className="ml-auto text-[11px] text-slate-400">Includes your benchmarks and hand edits · a re-upload never overwrites them</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Compare — 2+ vendors side by side. Matched rows (auto by KPI text, or a
// user pairing), standings strip, month picker, vendor-only sections with
// "Pair…" so the user adds measures to the comparison, and the trend chart.
// ---------------------------------------------------------------------------
function CompareView({ vendorIds, year, onClose }) {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [pairBase, setPairBase] = useState(null); // { metric_id, kpi_text, vendorId }
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    jfetch(`/scorecards/compare?year=${year}&ids=${vendorIds.join(',')}`).then((d) => {
      setData(d);
      setMonth((m) => {
        if (m) return m;
        const latest = Math.max(0, ...d.vendors.map((v) => v.through_month || 0));
        return latest || null;
      });
    }).catch((e) => setErr(e.message));
  }, [vendorIds, year]);
  useEffect(() => { load(); }, [load]);

  const colorOf = useMemo(() => {
    const map = {};
    (data?.vendors || []).forEach((v, i) => { map[v.id] = VENDOR_COLORS[i % VENDOR_COLORS.length]; });
    return map;
  }, [data]);

  if (!data) {
    return createPortal(
      <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8 text-xs text-slate-400">{err || 'Building the comparison…'}</div>
      </div>, document.body);
  }

  const vendors = data.vendors;
  const maxMonth = Math.max(0, ...vendors.map((v) => v.through_month || 0));
  const monthsWithData = Array.from({ length: maxMonth }, (_, i) => i + 1);
  const needle = q.trim().toLowerCase();
  const allRows = data.rows.filter((r) => !needle || r.label.toLowerCase().includes(needle) || (r.category || '').toLowerCase().includes(needle));
  const shared = allRows.filter((r) => Object.keys(r.by_vendor).length >= 2);
  const soloByVendor = vendors.map((v) => ({
    vendor: v,
    rows: allRows.filter((r) => Object.keys(r.by_vendor).length === 1 && r.by_vendor[v.id]),
  })).filter((g) => g.rows.length > 0);

  const cellFor = (r, vid) => {
    const entry = r.by_vendor[vid];
    if (!entry) return <span className="text-slate-300">—</span>;
    const s = entry.scores[month];
    if (!s) return <span className="text-slate-300">—</span>;
    const text = entry.kind === 'percent' && s.num != null ? `${Math.round(Number(s.num) * 1000) / 10}%` : (s.raw ?? '');
    const cls = s.met === false ? 'bg-rose-50 text-rose-700' : s.met === true ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700';
    return <span className={`inline-block min-w-[3rem] px-2 py-0.5 rounded-md text-[11.5px] font-semibold ${cls}`}>{text}</span>;
  };

  const sheetsInOrder = [...new Set(shared.map((r) => r.sheet))];

  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(78rem,97vw)] max-h-[93vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div>
            <div className="text-[15px] font-bold text-slate-900">Compare · {vendors.map((v) => v.name).join(' · ')}</div>
            <div className="text-[11.5px] text-slate-500">{shared.length} matched measures · {year}. Pair anything unmatched below — pairings persist.</div>
          </div>
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            {monthsWithData.map((m) => (
              <button key={m} onClick={() => setMonth(m)}
                className={`px-2.5 py-1 rounded-lg text-[11.5px] font-semibold ${month === m ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {MONTHS[m - 1]}
              </button>
            ))}
            <button onClick={onClose} aria-label="Close" className="ml-2 w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 text-sm">✕</button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          {err && <div className="mb-2 text-[11px] font-semibold text-rose-600">{err}</div>}

          {/* Standings strip */}
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(vendors.length, 4)}, minmax(0, 1fr))` }}>
            {vendors.map((v) => {
              const mrate = rateFor(v.months[month]);
              const prate = rateFor(v.months[month - 1]);
              return (
                <div key={v.id} className="bg-white border border-slate-200 rounded-2xl p-3.5">
                  <div className="text-[12.5px] font-bold text-slate-800 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ background: colorOf[v.id] }} />
                    {v.name}
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-[22px] font-bold text-slate-900 tabular-nums">{pctText(mrate)}</span>
                    <DeltaArrow delta={mrate != null && prate != null ? mrate - prate : null} />
                    <span className="text-[10.5px] text-slate-400 ml-auto">met in {month ? MONTHS[month - 1] : '—'}</span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {v.domains.map((d) => {
                      const r = d.total ? d.met / d.total : null;
                      return (
                        <div key={d.sheet} className="flex items-center gap-2 text-[10.5px] text-slate-500">
                          <span className="w-24 truncate" title={d.sheet}>{d.sheet}</span>
                          <span className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <span className="block h-full rounded-full" style={{ width: `${Math.round((r || 0) * 100)}%`, background: r == null ? '#cbd5e1' : r >= 0.95 ? '#047857' : r >= 0.85 ? '#d97706' : '#be123c' }} />
                          </span>
                          <span className="w-8 text-right tabular-nums font-semibold text-slate-700">{r == null ? '—' : Math.round(r * 100)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Matched grid */}
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-3.5 py-2.5 border-b border-slate-100 flex items-center gap-2">
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search measures…"
                className="w-64 text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-slate-50 focus:bg-white focus:outline-none focus:border-orange-400" />
              <span className="ml-auto text-[10.5px] text-slate-400">{month ? MONTHS[month - 1] : ''} {year} · red = missed benchmark</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" style={{ minWidth: 640 }}>
                <thead>
                  <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50/60">
                    <th className="text-left py-2 px-3.5">Measure</th>
                    {vendors.map((v) => <th key={v.id} className="text-center py-2 px-2 w-32">{v.name}</th>)}
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {sheetsInOrder.map((sh) => (
                    <React.Fragment key={sh}>
                      <tr><td colSpan={vendors.length + 2} className="pt-3 pb-1 px-3.5 text-[10px] font-bold uppercase tracking-wider text-orange-700">{sh}</td></tr>
                      {shared.filter((r) => r.sheet === sh).map((r) => (
                        <tr key={r.key} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="py-1.5 px-3.5 text-slate-700 leading-snug">
                            {r.label}
                            {r.link_id && (
                              <button title="Paired by a user — click to unpair" disabled={busy} onClick={async () => {
                                setBusy(true);
                                try { await jfetch(`/scorecards/compare/links/${r.link_id}`, { method: 'DELETE' }); load(); } catch (e) { setErr(e.message); }
                                setBusy(false);
                              }} className="ml-1.5 text-violet-400 hover:text-violet-600 text-[10px] font-semibold">paired ⊗</button>
                            )}
                          </td>
                          {vendors.map((v) => <td key={v.id} className="text-center py-1.5 px-2">{cellFor(r, v.id)}</td>)}
                          <td />
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {shared.length === 0 && (
                    <tr><td colSpan={vendors.length + 2} className="py-8 text-center text-slate-400">No matched measures{needle ? ' for this search' : ''}. Pair measures below to build the comparison.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Vendor-only measures + pairing */}
          {soloByVendor.map((g) => (
            <details key={g.vendor.id} className="mt-3 bg-slate-50/70 border border-slate-200 rounded-2xl px-4 py-2.5">
              <summary className="cursor-pointer text-[11.5px] font-semibold text-slate-600">
                Only on {g.vendor.name}&apos;s card · {g.rows.length} measure{g.rows.length === 1 ? '' : 's'} — pair one to compare it
              </summary>
              <div className="mt-2 space-y-1">
                {g.rows.map((r) => (
                  <div key={r.key} className="flex items-center gap-2 text-[11.5px] text-slate-600 py-0.5">
                    <span className="flex-1 leading-snug">{r.label}</span>
                    <button onClick={() => setPairBase({ metric_id: r.by_vendor[g.vendor.id].metric_id, kpi_text: r.label, vendorId: g.vendor.id })}
                      className="px-2 py-0.5 rounded-md bg-white border border-slate-300 hover:border-orange-400 text-slate-600 text-[10.5px] font-semibold">
                      Pair…
                    </button>
                  </div>
                ))}
              </div>
            </details>
          ))}

          {/* Trend overlay */}
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center gap-4 flex-wrap text-[11px] text-slate-500 mb-1">
              {vendors.map((v) => (
                <span key={v.id}><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: colorOf[v.id] }} />{v.name}</span>
              ))}
              <span className="ml-auto text-slate-400">Measures met by month · {year}</span>
            </div>
            <svg viewBox="0 0 900 120" style={{ width: '100%', height: 120 }} preserveAspectRatio="none" aria-label="Met rate by month for each vendor">
              {[30, 60, 90].map((gy) => <line key={gy} x1="0" y1={gy} x2="900" y2={gy} stroke="#eef1f5" />)}
              {vendors.map((v) => {
                const pts = [];
                for (let m = 1; m <= maxMonth; m++) {
                  const r = rateFor(v.months[m]);
                  if (r != null) pts.push([m, r]);
                }
                if (pts.length < 2) return null;
                const xs = (m) => 30 + ((m - 1) / Math.max(1, maxMonth - 1)) * 840;
                const ys = (r) => 105 - r * 90;
                return (
                  <g key={v.id}>
                    <polyline points={pts.map(([m, r]) => `${xs(m)},${ys(r)}`).join(' ')} fill="none" stroke={colorOf[v.id]} strokeWidth="2.5" />
                    <circle cx={xs(pts[pts.length - 1][0])} cy={ys(pts[pts.length - 1][1])} r="4" fill={colorOf[v.id]} />
                  </g>
                );
              })}
              {monthsWithData.map((m) => (
                <text key={m} x={30 + ((m - 1) / Math.max(1, maxMonth - 1)) * 840} y="118" fontSize="10" fill="#94a3b8" textAnchor="middle">{MONTHS[m - 1]}</text>
              ))}
            </svg>
          </div>
        </div>
      </div>

      {pairBase && (
        <PairPicker
          base={pairBase}
          vendors={vendors}
          rows={data.rows}
          onDone={() => { setPairBase(null); load(); }}
          onCancel={() => setPairBase(null)}
        />
      )}
    </div>,
    document.body
  );
}

/** Pick the matching metric on each other vendor for a user pairing. */
function PairPicker({ base, vendors, rows, onDone, onCancel }) {
  const [chosen, setChosen] = useState({}); // vendorId -> metric_id
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const others = vendors.filter((v) => v.id !== base.vendorId);
  const needle = q.trim().toLowerCase();

  // Any of the vendor's metrics not already in a user pairing can be the
  // counterpart — including ones that auto-matched with a different vendor
  // (the common case: two vendors word a KPI identically, the third differs).
  // Likely matches float up: sorted by shared words with the base KPI.
  const baseWords = new Set(base.kpi_text.toLowerCase().split(/\W+/).filter((w) => w.length > 3));
  const overlap = (label) => label.toLowerCase().split(/\W+/).filter((w) => baseWords.has(w)).length;
  const candidatesFor = (vid) => rows
    .filter((r) => !r.link_id && r.by_vendor[vid] && r.by_vendor[vid].metric_id !== base.metric_id)
    .map((r) => ({ metric_id: r.by_vendor[vid].metric_id, label: r.by_vendor[vid].kpi_text || r.label }))
    .filter((c) => !needle || c.label.toLowerCase().includes(needle))
    .sort((a, b) => overlap(b.label) - overlap(a.label));

  const save = async () => {
    const ids = Object.values(chosen).filter(Boolean);
    if (!ids.length) return;
    setBusy(true);
    try {
      await jfetch('/scorecards/compare/links', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: base.kpi_text, metric_ids: [base.metric_id, ...ids] }),
      });
      onDone();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/45 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(46rem,95vw)] max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-slate-200">
          <div className="text-[13.5px] font-bold text-slate-900">Pair this measure</div>
          <div className="mt-0.5 text-[11.5px] text-slate-500 leading-snug">&ldquo;{base.kpi_text}&rdquo;</div>
          <div className="mt-1 text-[10.5px] text-slate-400">Pick its counterpart on each vendor — the paired measures become one comparison row. Unpair anytime.</div>
        </div>
        <div className="px-5 pt-3">
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-slate-50 focus:bg-white focus:outline-none focus:border-orange-400" />
          {err && <div className="mt-1.5 text-[11px] font-semibold text-rose-600">{err}</div>}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-3 space-y-3">
          {others.map((v) => {
            const cands = candidatesFor(v.id);
            return (
              <div key={v.id}>
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-1">{v.name}</div>
                {cands.length === 0 && <div className="text-[11px] text-slate-400">No unmatched measures{needle ? ' for this search' : ''}.</div>}
                <div className="space-y-0.5">
                  {cands.slice(0, 40).map((c) => (
                    <label key={c.metric_id} className="flex items-start gap-2 text-[11.5px] text-slate-700 cursor-pointer py-0.5">
                      <input type="radio" name={`pair-${v.id}`} checked={chosen[v.id] === c.metric_id}
                        onChange={() => setChosen((prev) => ({ ...prev, [v.id]: prev[v.id] === c.metric_id ? null : c.metric_id }))}
                        className="mt-0.5 accent-orange-600" />
                      <span className="leading-snug">{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center gap-2">
          <button onClick={save} disabled={busy || !Object.values(chosen).some(Boolean)}
            className="px-3.5 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
            {busy ? 'Pairing…' : 'Pair measures'}
          </button>
          <button onClick={onCancel} className="px-3.5 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-600 text-xs font-semibold">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scorecards view — the standings roster: met rate, direction, sparkline.
// Tick vendors to compare; click a card to open the scorecard modal.
// ---------------------------------------------------------------------------
function ScorecardsView() {
  const [vendors, setVendors] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [openVendor, setOpenVendor] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [compareIds, setCompareIds] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [busyMsg, setBusyMsg] = useState('');
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(() => {
    jfetch(`/scorecards?year=${year}`).then((d) => setVendors(d.vendors)).catch((e) => setError(e.message));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const doUpload = async (file) => {
    if (!file || !uploadFor) return;
    setBusyMsg(`Ingesting ${file.name}…`); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('year', String(uploadYear));
      const d = await jfetch(`/scorecards/${uploadFor}/upload`, { method: 'POST', body: fd });
      setOpenVendor(uploadFor);
      setUploadFor(null);
      setYear(uploadYear);
      load();
      setBusyMsg(`Ingested: ${d.stats.cells} scores across ${d.stats.months.length} month(s), ${d.stats.missed} missed benchmark(s)${d.stats.skipped_user_cells ? `, ${d.stats.skipped_user_cells} hand-entered cell(s) preserved` : ''}.`);
      setTimeout(() => setBusyMsg(''), 8000);
    } catch (e) { setBusyMsg(''); setError(e.message); }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const withScorecards = vendors.filter((v) => v.cells > 0 || v.last_upload_at);
  const without = vendors.filter((v) => !v.cells && !v.last_upload_at);

  return (
    <div className="p-8 pt-4 max-w-7xl mx-auto w-full">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <p className="text-[11.5px] text-slate-400">The vendor&apos;s own numbers, measured against your benchmarks. Upload the workbook as it arrives — misses flag red, and anything you set by hand survives every re-upload.</p>
        <div className="flex items-center gap-2.5">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="text-[11.5px] border border-slate-200 rounded-lg px-2 py-1 bg-white">
            {[year + 1, year, year - 1, year - 2].filter((y, i, a) => a.indexOf(y) === i).sort((a, b) => b - a).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {busyMsg && <span className="text-[11.5px] font-semibold text-emerald-700">{busyMsg}</span>}
          {error && <span className="text-[11.5px] font-semibold text-rose-600">{error}</span>}
        </div>
      </div>
      <input type="file" ref={fileRef} accept=".xlsx,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; doUpload(f); }} />
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {withScorecards.map((v) => {
          // The big number is the YEAR TO DATE met rate across every month
          // with data (operator order 2026-10-20): a September of all-met
          // never hides a spring of misses. The latest month rides beside it.
          const latest = latestRate(v.monthly);
          const ytd = yearToDateRate(v.monthly);
          const rate = ytd.rate;
          const sparkValues = (v.monthly || []).length >= 2
            ? Array.from({ length: Math.max(...v.monthly.map((m) => m.month)) }, (_, i) => rateFor((v.monthly || []).find((m) => m.month === i + 1)))
            : null;
          const isSel = selected.has(v.id);
          return (
            <div key={v.id} className={`bg-white border rounded-2xl p-4 cursor-pointer transition-all ${isSel ? 'border-orange-400 ring-2 ring-orange-100' : 'border-slate-200 hover:shadow-md'}`}
              onClick={() => setOpenVendor(v.id)}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[13.5px] font-semibold text-slate-900">{v.name}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {v.through_month ? `Through ${MONTHS[v.through_month - 1]} ${year}` : 'No data this year'}
                    {v.last_upload_at ? ` · received ${new Date(v.last_upload_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                  </div>
                </div>
                <button aria-label={isSel ? 'Remove from comparison' : 'Add to comparison'} title="Tick to compare"
                  onClick={(e) => { e.stopPropagation(); toggleSelect(v.id); }}
                  className={`w-[18px] h-[18px] rounded-md border flex-none grid place-items-center text-[11px] ${isSel ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 text-transparent hover:border-orange-400'}`}>
                  ✓
                </button>
              </div>
              {rate != null && (
                <div className="mt-2.5 flex items-baseline gap-2">
                  <span className="text-[24px] font-bold text-slate-900 tabular-nums leading-none">{pctText(rate)}</span>
                  <span className="text-[10.5px] text-slate-400">met year to date · {ytd.met} of {ytd.total}</span>
                  {latest.rate != null && (
                    <span className="text-[10.5px] text-slate-500 ml-auto inline-flex items-baseline gap-1">
                      {MONTHS[latest.month - 1]} {pctText(latest.rate)} <DeltaArrow delta={latest.delta} />
                    </span>
                  )}
                </div>
              )}
              {sparkValues && <div className="mt-1.5"><Spark values={sparkValues} color={rate != null && rate < 0.9 ? '#be123c' : '#047857'} width={240} height={30} /></div>}
              <div className="mt-2 flex items-center gap-2">
                <MissBadge missed={v.missed} />
                <button onClick={(e) => { e.stopPropagation(); setUploadFor(v.id); setUploadYear(year); setTimeout(() => fileRef.current?.click(), 0); }}
                  className="ml-auto px-2.5 py-1 rounded-lg bg-white border border-orange-300 hover:border-orange-500 text-orange-700 text-[11px] font-semibold">
                  Upload workbook
                </button>
              </div>
            </div>
          );
        })}
        {without.length > 0 && (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">No scorecard yet</div>
            <div className="flex flex-wrap gap-1.5">
              {without.map((v) => (
                <button key={v.id} onClick={() => { setUploadFor(v.id); setUploadYear(year); setTimeout(() => fileRef.current?.click(), 0); }}
                  className="px-2.5 py-1 rounded-lg bg-white border border-slate-300 hover:border-orange-400 text-slate-600 text-[11.5px] font-semibold">
                  {v.name} ↑
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {selected.size >= 2 && (
        <button onClick={() => setCompareIds([...selected])}
          className="mt-4 px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-[12.5px] font-semibold shadow-sm">
          Compare {selected.size} selected →
        </button>
      )}
      {selected.size === 1 && <p className="mt-3 text-[11px] text-slate-400">Tick one more vendor to compare.</p>}

      {openVendor && (
        <ScorecardDetail
          vendor={vendors.find((v) => v.id === openVendor)}
          vendors={vendors}
          year={year} onYear={setYear} onChanged={load}
          onClose={() => setOpenVendor(null)}
          onCompareWith={(otherId) => { setOpenVendor(null); setCompareIds([openVendor, otherId]); }}
        />
      )}
      {compareIds && <CompareView vendorIds={compareIds} year={year} onClose={() => setCompareIds(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Correspondence (operator order 2026-09-18): per vendor, per task, in date
// order. Workstream emails as sent and the vendor's replies file themselves;
// a person attaches a file with a note. Emails open and download exactly as
// they do on an assessment.
// ---------------------------------------------------------------------------
const TASK_LABEL = { scorecards: 'Scorecards', work_plan: 'Work plan' };
const KIND_LABEL = { email_sent: 'Email sent', email_reply: 'Reply received', attachment: 'File attached' };

function CorrespondenceEntry({ c }) {
  const [open, setOpen] = useState(false);
  const when = new Date(c.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  const tone = c.kind === 'email_reply' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : c.kind === 'attachment' ? 'bg-slate-100 text-slate-600 ring-slate-200' : 'bg-orange-50 text-orange-700 ring-orange-200';
  const list = (v) => (Array.isArray(v) ? v.join(', ') : v || '');
  return (
    <div className="border border-slate-200 rounded-xl bg-white px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wide ring-1 ${tone}`}>{KIND_LABEL[c.kind] || c.kind}</span>
        <span className="text-[11px] text-slate-400">{when}</span>
        <span className="text-[13px] font-semibold text-slate-800 ml-1">{c.subject || c.document_file_name || '(no subject)'}</span>
        <span className="ml-auto text-[11px] text-slate-400">{c.kind === 'email_reply' ? c.from_email : c.created_by_name || c.created_by_email || ''}</span>
      </div>
      {c.kind !== 'attachment' && (
        <div className="mt-1.5 space-y-0.5 text-[10.5px]">
          {c.from_email && c.kind === 'email_sent' && <div><span className="text-slate-400 uppercase tracking-wide font-semibold w-10 inline-block">From</span><span className="text-slate-600">{c.from_email}</span></div>}
          {list(c.to_emails) && <div><span className="text-slate-400 uppercase tracking-wide font-semibold w-10 inline-block">To</span><span className="text-slate-600">{list(c.to_emails)}</span></div>}
          {list(c.cc_emails) && <div><span className="text-slate-400 uppercase tracking-wide font-semibold w-10 inline-block">Cc</span><span className="text-slate-600">{list(c.cc_emails)}</span></div>}
        </div>
      )}
      {c.note && <div className="mt-1.5 text-[12px] text-slate-700">{c.note}</div>}
      {c.kind === 'email_reply' && c.body_text && (
        <div className="mt-1.5 text-[12px] text-slate-700 whitespace-pre-wrap border-l-2 border-emerald-200 pl-2.5">{open ? c.body_text : c.body_text.slice(0, 400)}{c.body_text.length > 400 && !open ? '…' : ''}</div>
      )}
      <div className="mt-2 flex items-center gap-3 flex-wrap text-[10.5px] font-semibold">
        {c.kind === 'email_sent' && c.body_html && (
          <button type="button" onClick={() => setOpen(!open)} className="text-orange-700 hover:text-orange-900">{open ? 'Close email' : 'Open email'}</button>
        )}
        {c.kind === 'email_reply' && c.body_text && c.body_text.length > 400 && (
          <button type="button" onClick={() => setOpen(!open)} className="text-orange-700 hover:text-orange-900">{open ? 'Show less' : 'Read the whole reply'}</button>
        )}
        {c.document_id && c.kind === 'email_sent' && (c.document_available
          ? <a href={`${API}/api/fdr/documents/${c.document_id}/download`} className="text-orange-700 hover:text-orange-900 no-underline">Download email (.eml)</a>
          : <span className="text-slate-400">Email file purged</span>)}
        {c.document_id && c.kind === 'attachment' && (c.document_available
          ? <a href={`${API}/api/fdr/documents/${c.document_id}/download`} className="text-orange-700 hover:text-orange-900 no-underline">Download {c.document_file_name}{c.document_size ? ` (${Math.max(1, Math.round(c.document_size / 1024))} KB)` : ''}</a>
          : <span className="text-slate-400">File purged</span>)}
        {(Array.isArray(c.attachments) ? c.attachments : []).map((a) => (
          <a key={a.document_id} href={`${API}/api/fdr/documents/${a.document_id}/download`} className="text-orange-700 hover:text-orange-900 no-underline">Attachment: {a.file_name}</a>
        ))}
      </div>
      {open && c.kind === 'email_sent' && c.body_html && (
        <iframe title="Sent email" sandbox="" srcDoc={c.body_html} className="w-full bg-white mt-2 border border-slate-200 rounded-lg" style={{ height: 480 }} />
      )}
    </div>
  );
}

function CorrespondenceView() {
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [task, setTask] = useState('scorecards');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    // Every vendor of the organization, whatever its status: letters with a
    // suspended or terminated vendor belong on the record too (operator order
    // 2026-09-21). Active vendors first, then the rest, each by name.
    jfetch('/entities').then((d) => {
      const rank = (e) => (String(e.status || '').toLowerCase() === 'active' || Number(e.active_contracts_count) > 0 ? 0 : 1);
      const list = (d.entities || []).slice().sort((a, b) => rank(a) - rank(b) || String(a.name).localeCompare(String(b.name)));
      setEntities(list);
      if (list.length && !entityId) setEntityId(list[0].id);
    }).catch((e) => setError(e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    if (!entityId) return;
    setRows(null);
    jfetch(`/correspondence?entity_id=${encodeURIComponent(entityId)}&task=${task}`).then((d) => setRows(d.rows || [])).catch((e) => { setError(e.message); setRows([]); });
  }, [entityId, task]);
  useEffect(() => { load(); }, [load]);

  const attach = async (file) => {
    if (!file || !entityId) return;
    setBusy(`Attaching ${file.name}…`); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('task', task);
      fd.append('note', note);
      await jfetch(`/correspondence/${entityId}/files`, { method: 'POST', body: fd });
      setNote('');
      load();
      setBusy('');
    } catch (e) { setError(e.message); setBusy(''); }
  };

  const vendor = entities.find((e) => e.id === entityId);
  return (
    <div className="px-8 py-5 max-w-5xl">
      <div className="flex items-end gap-3 flex-wrap">
        <label className="text-xs">
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-1">Vendor</div>
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm min-w-[260px]">
            {entities.map((e) => {
              const st = String(e.status || '').trim();
              const label = st ? st.charAt(0).toUpperCase() + st.slice(1).replace(/_/g, ' ') : 'No status';
              const contracts = Number(e.active_contracts_count) || 0;
              return <option key={e.id} value={e.id}>{e.name} · {label}{contracts ? ` · ${contracts} active contract${contracts === 1 ? '' : 's'}` : ''}</option>;
            })}
          </select>
        </label>
        <div className="flex items-center gap-1.5">
          {Object.keys(TASK_LABEL).map((t) => (
            <button key={t} onClick={() => setTask(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${task === t ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {TASK_LABEL[t]}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-end gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the file (optional)"
            className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs w-64" />
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => { attach(e.target.files?.[0]); e.target.value = ''; }} />
          <button type="button" disabled={!entityId || !!busy} onClick={() => fileRef.current?.click()}
            className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
            {busy || 'Attach a file'}
          </button>
        </div>
      </div>
      <p className="mt-2 text-[11.5px] text-slate-400">
        Everything sent to and received from {vendor ? vendor.name : 'this vendor'} about {TASK_LABEL[task].toLowerCase()}, oldest first. Workstream emails and their replies file here on their own; files you attach sit beside them.
      </p>
      {error && <div className="mt-3 text-xs text-rose-600">{error}</div>}
      <div className="mt-4 space-y-2">
        {rows === null ? <div className="text-xs text-slate-400">Loading…</div>
          : rows.length === 0 ? <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl px-4 py-8 text-center">Nothing on file yet for {TASK_LABEL[task].toLowerCase()} with {vendor ? vendor.name : 'this vendor'}. Send a Workstream follow up, or attach a file.</div>
          : rows.map((c) => <CorrespondenceEntry key={c.id} c={c} />)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The COA tab: the REAL work plan (the existing COA engine, FDR-scoped —
// the same one-engine pattern as Audits) and Scorecards as its own view.
// ---------------------------------------------------------------------------
export default function OversightCoa({ user = null }) {
  const [sub, setSub] = useState('workplan');
  return (
    // The work plan flows with the FDR page (one scroll, see COAWorkPlan);
    // Scorecards, Charting and Correspondence keep their own pane.
    <div className={sub === 'workplan' ? 'flex-1 flex flex-col min-w-0' : 'flex-1 min-h-0 flex flex-col overflow-y-auto'}>
      <div className="px-8 pt-4 flex items-center gap-1.5">
        <button onClick={() => setSub('workplan')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${sub === 'workplan' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          Work plan
        </button>
        <button onClick={() => setSub('scorecards')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${sub === 'scorecards' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          Scorecards
        </button>
        <button onClick={() => setSub('charting')}
          title="Trends on the FDR work plan measures and on the vendors' scorecards, with the calendar table and the PDF"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${sub === 'charting' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          Charting
        </button>
        <button onClick={() => setSub('correspondence')}
          title="Emails sent and received, and files, per vendor, for scorecards and the work plan"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${sub === 'correspondence' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          Correspondence
        </button>
      </div>
      {sub === 'workplan' ? <COAWorkPlan scope="fdr" user={user} />
        : sub === 'scorecards' ? <ScorecardsView />
        : sub === 'correspondence' ? <CorrespondenceView />
        : <FdrCharting orgName={user?.organization_name || ''} />}
    </div>
  );
}
